import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Tab,
  Tabs,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import axios from 'axios';
import DashboardLayout from '../../components/layout/DashboardLayout';
import config from '../../config';
import systemSettingsService from '../../services/systemSettingsService';
import waitingLocationService, { WaitingLocation } from '../../services/waitingLocationService';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';
import { Hotel, OptionSetting, SystemSettings as SystemSettingsType, BusinessHour } from '../../types/system';
import type { SystemSettingsResponse } from '../../types/systemSettings';
import { notification } from 'antd';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useBusinessDate } from '../../hooks/useBusinessDate';
import userService from '../../services/userService';
import {
  getPendingFollowers,
  getLinkedUsers,
  linkUser,
  unlinkUser,
  sendTestMessage,
  PendingFollower,
  LinkedUser,
} from '../../services/lineService';

// サービス層の既存インターフェース
interface SystemSettings {      
  maintenance_mode: boolean;    
  system_email: string;
  backup_enabled: boolean;      
  backup_frequency: string;     
  backup_retention_days: number;
  log_level: string;
  max_upload_size: number;      
  allowed_file_types: string[];
  [key: string]: any; // APIからの追加プロパティを許容
}

interface NotificationSettings {
  email: boolean;
  slack: boolean;
  line: boolean;
  emailSettings?: {
    host: string;
    port: number;
    username: string;
    password: string;
  };
  slackSettings?: {
    webhookUrl: string;
    channel: string;
  };
  lineSettings?: {
    channelId: string;
    channelSecret: string;
    accessToken: string;
  };
  [key: string]: any; // APIからの追加プロパティを許容
}

interface LineSettings {
  channelId: string;
  channelSecret: string;
  accessToken: string;
  [key: string]: any;
}

interface BackupSettings {
  autoBackup: boolean;
  backupInterval: number;
  maxBackups: number;
  [key: string]: any;
}

// 画面表示用の拡張インターフェース
interface FormSystemSettings extends Omit<SystemSettingsType, 'hotels' | 'options' | 'business_hours'> {
  hotels: Hotel[];
  options: OptionSetting[];
  business_hours: BusinessHour[];
  notificationSettings?: NotificationSettings;
  lineSettings?: LineSettings;
  backupSettings?: BackupSettings;
}

interface FormNotificationSettings extends NotificationSettings {
  orderNotifications: boolean;
  shiftNotifications: boolean;
}

interface FormLineSettings extends LineSettings {
  enabled: boolean;
  notifyOnNewOrder: boolean;
  notifyOnOrderStatus: boolean;
  notifyOnShiftChange: boolean;
}

interface FormBackupSettings extends BackupSettings {
  backupTime: string;
  retentionDays: number;
  backups: Array<{
    id: string;
    created_at: string;
    size: string;
  }>;
}

// バックアップエントリの型
interface BackupEntry {
  id: string;
  created_at: string;
  size: string;
}

// コース設定の型定義
interface CourseItem {
  id: string; // 一意のID
  name?: string; // コース名
  duration: number; // 分数
  castback: number; // キャストバック金額
  revenue: number; // 収益
}

// 通知用の関数を追加
const useNotification = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');

  const showNotification = (
    notificationMessage: string,
    notificationSeverity: 'success' | 'info' | 'warning' | 'error' = 'success'
  ) => {
    setMessage(notificationMessage);
    setSeverity(notificationSeverity);
    setOpen(true);
  };

  const notification = {
    success: (config: { message: string; description?: string }) => {
      showNotification(config.description || config.message, 'success');
    },
    error: (config: { message: string; description?: string }) => {
      showNotification(config.description || config.message, 'error');
    },
    info: (config: { message: string; description?: string }) => {
      showNotification(config.description || config.message, 'info');
    },
    warning: (config: { message: string; description?: string }) => {
      showNotification(config.description || config.message, 'warning');
    }
  };

  const NotificationComponent = () => (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={() => setOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setOpen(false)} severity={severity}>
        {message}
      </Alert>
    </Snackbar>
  );

  return { notification, NotificationComponent };
};

// API_BASE_URLの参照を修正
const apiBaseUrl = config.API_BASE_URL;

// 初期状態の定義
const initialFormData: FormSystemSettings = {
  store_name: '',
  phone_number: '',
  store_address: '',
  line_account: '',
  language: 'ja',
  theme: 'light',
  notification_enabled: false,
  line_notification_enabled: false,
  line_notifications_enabled: false,
  email_notifications_enabled: false,
  push_notifications_enabled: false,
  sms_notifications_enabled: false,
  notification_sound_enabled: false,
  gps_tracking_interval: 60,
  map_default_zoom: 13,
  default_map_latitude: 0,
  default_map_longitude: 0,
  default_map_radius: 0,
  business_hours_start: '09:00',
  business_hours_end: '22:00',
  maintenance_mode: false,
  system_email: '',
  backup_enabled: false,
  backup_frequency: 'daily',
  backup_retention_days: 7,
  log_level: 'info',
  max_upload_size: 10,
  allowed_file_types: ['jpg', 'png', 'pdf'],
  notify_new_orders: false,
  notify_shifts: false,
  notify_messages: false,
  courses: [],
  hotels: [],
  options: [],
  business_hours: [],
  allow_same_day_bookings: true,
  max_booking_days_ahead: 30,
  min_booking_interval: 30,
  cancellation_deadline_hours: 24,
  cancellation_fee_rate: 100,
  service_fee_rate: 0,
  tax_rate: 10,
  distance_fee_enabled: false,
  distance_fee_per_km: 0,
  auto_backup_enabled: false,
  backup_interval_hours: 24,
  last_backup_at: null,
  line_channel_id: null,
  line_channel_secret: null,
  line_channel_token: null,
  line_webhook_url: null,
  created_at: '',
  updated_at: '',
  notificationSettings: {
    email: false,
    slack: false,
    line: false
  },
  lineSettings: {
    channelId: '',
    channelSecret: '',
    accessToken: '',
    enabled: false,
    notifyOnNewOrder: false,
    notifyOnOrderStatus: false,
    notifyOnShiftChange: false
  },
  backupSettings: {
    autoBackup: true,
    backupInterval: 1,
    maxBackups: 5,
    backupTime: '00:00',
    retentionDays: 30,
    backups: []
  }
};

type NewHotel = Omit<Hotel, 'id'>;

const SystemSettings = () => {
  const { notification, NotificationComponent } = useNotification();
  const [formData, setFormData] = useState<FormSystemSettings>({
    store_name: '',
    store_address: '',
    phone_number: '',
    line_account: '',
    language: 'ja',
    theme: 'light',
    notification_enabled: false,
    line_notification_enabled: false,
    line_notifications_enabled: false,
    email_notifications_enabled: false,
    push_notifications_enabled: false,
    sms_notifications_enabled: false,
    notification_sound_enabled: false,
    gps_tracking_interval: 60,
    default_map_latitude: 35.6895,
    default_map_longitude: 139.6917,
    default_map_radius: 5000,
    map_default_zoom: 13,
    business_hours_start: '09:00',
    business_hours_end: '22:00',
    maintenance_mode: false,
    system_email: '',
    backup_enabled: false,
    backup_frequency: 'daily',
    log_level: 'info',
    max_upload_size: 10,
    allowed_file_types: ['jpg', 'png', 'pdf'],
    notify_new_orders: false,
    notify_shifts: false,
    notify_messages: false,
    courses: [],
    hotels: [],
    options: [],
    business_hours: [],
    allow_same_day_bookings: true,
    max_booking_days_ahead: 30,
    min_booking_interval: 30,
    cancellation_deadline_hours: 24,
    cancellation_fee_rate: 100,
    service_fee_rate: 0,
    tax_rate: 10,
    distance_fee_enabled: false,
    distance_fee_per_km: 0,
    auto_backup_enabled: false,
    backup_interval_hours: 24,
    backup_retention_days: 7,
    last_backup_at: null,
    line_channel_id: null,
    line_channel_secret: null,
    line_channel_token: null,
    line_webhook_url: null,
    created_at: '',
    updated_at: '',
    notificationSettings: {
      email: false,
      slack: false,
      line: false,
      orderNotifications: false,
      shiftNotifications: false
    },
    lineSettings: {
      channelId: '',
      channelSecret: '',
      accessToken: '',
      enabled: false,
      notifyOnNewOrder: false,
      notifyOnOrderStatus: false,
      notifyOnShiftChange: false
    },
    backupSettings: {
      autoBackup: true,
      backupInterval: 1,
      maxBackups: 5,
      backupTime: '00:00',
      retentionDays: 30,
      backups: []
    }
  });
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null);

  // LINE連携管理用のstate
  const [pendingFollowers, setPendingFollowers] = useState<PendingFollower[]>([]);
  const [linkedUsers, setLinkedUsers] = useState<LinkedUser[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [linkingTarget, setLinkingTarget] = useState<{ follower: PendingFollower; userId: number | null }>({ follower: { id: 0, line_user_id: '', display_name: null, followed_at: null }, userId: null });
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [lineLoading, setLineLoading] = useState(false);
  const [savingCourses, setSavingCourses] = useState(false);
  const [savingBusinessHours, setSavingBusinessHours] = useState(false);
  const [businessHours, setBusinessHours] = useState<BusinessHour[]>([]);
  const [isHotelDialogOpen, setIsHotelDialogOpen] = useState(false);
  const [newHotel, setNewHotel] = useState<NewHotel>({
    name: '',
    name_kana: '',
    address: '',
    phone_number: '',
    description: '',
    is_active: true,
    latitude: null,
    longitude: null,
    area: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });
  const [isOptionDialogOpen, setIsOptionDialogOpen] = useState(false);
  const [newOption, setNewOption] = useState<Omit<OptionSetting, 'id'>>({ name: '', price: 0, category: 'standard', description: '', castback: 0 });

  // 編集・削除ダイアログ用のstate
  const [isEditHotelDialogOpen, setIsEditHotelDialogOpen] = useState(false);
  const [editingHotel, setEditingHotel] = useState<Hotel | null>(null);
  const [isDeleteHotelDialogOpen, setIsDeleteHotelDialogOpen] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState<Hotel | null>(null);

  // 待機場所マスタ
  const [waitingLocations, setWaitingLocations] = useState<WaitingLocation[]>([]);
  const [isWaitingDialogOpen, setIsWaitingDialogOpen] = useState(false);
  const [newWaiting, setNewWaiting] = useState<{ name: string; name_kana: string; address: string }>({
    name: '', name_kana: '', address: '',
  });
  const [editingWaiting, setEditingWaiting] = useState<WaitingLocation | null>(null);
  const [isEditWaitingDialogOpen, setIsEditWaitingDialogOpen] = useState(false);
  const [savingWaiting, setSavingWaiting] = useState(false);

  const [isEditOptionDialogOpen, setIsEditOptionDialogOpen] = useState(false);
  const [editingOption, setEditingOption] = useState<OptionSetting | null>(null);
  const [isDeleteOptionDialogOpen, setIsDeleteOptionDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<OptionSetting | null>(null);


  const { businessDate } = useBusinessDate();
  const { settings, loading: settingsLoading } = useSystemSettings();

  const handleAddNewHotel = async () => {
    try {
      await systemSettingsService.addHotel(newHotel);
      setSuccess('ホテルを追加しました');
      setIsHotelDialogOpen(false);
      setNewHotel({
        name: '',
        name_kana: '',
        address: '',
        phone_number: '',
        description: '',
        is_active: true,
        latitude: null,
        longitude: null,
        area: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      fetchData(); // データを再読み込み
    } catch (error) {
      setError('ホテルの追加に失敗しました');
    }
  };

  // ホテル編集ハンドラ
  const handleOpenEditHotelDialog = (hotel: Hotel) => {
    setEditingHotel(hotel);
    setIsEditHotelDialogOpen(true);
  };

  const handleCloseEditHotelDialog = () => {
    setIsEditHotelDialogOpen(false);
    setEditingHotel(null);
  };

  // 編集中ホテルの住所から座標を再取得する
  const [regeocoding, setRegeocoding] = useState(false);
  const handleRegeocodeHotel = async () => {
    if (!editingHotel?.address) {
      setError('住所を入力してください');
      return;
    }
    setRegeocoding(true);
    try {
      const res = await axios.get(`/api/geocode?address=${encodeURIComponent(editingHotel.address)}`);
      const loc = res.data?.location;
      if (loc && loc.lat != null && loc.lng != null) {
        setEditingHotel(prev => prev ? { ...prev, latitude: loc.lat, longitude: loc.lng } : null);
        setSuccess('住所から座標を取得しました（必要なら手動で微調整してください）');
      } else {
        setError('座標を取得できませんでした');
      }
    } catch (e) {
      setError('座標の取得に失敗しました');
    } finally {
      setRegeocoding(false);
    }
  };

  const handleUpdateHotel = async () => {
    if (!editingHotel) return;
    try {
      await systemSettingsService.updateHotel(editingHotel.id, editingHotel);
      setSuccess('ホテル情報を更新しました');
      handleCloseEditHotelDialog();
      fetchData(); // データを再読み込み
    } catch (error) {
      setError('ホテル情報の更新に失敗しました');
    }
  };

  // ホテル削除ハンドラ
  const handleOpenDeleteHotelDialog = (hotel: Hotel) => {
    setHotelToDelete(hotel);
    setIsDeleteHotelDialogOpen(true);
  };

  const handleCloseDeleteHotelDialog = () => {
    setIsDeleteHotelDialogOpen(false);
    setHotelToDelete(null);
  };

  const handleDeleteHotel = async () => {
    if (!hotelToDelete) return;
    try {
      await systemSettingsService.deleteHotel(hotelToDelete.id);
      setSuccess('ホテルを削除しました');
      handleCloseDeleteHotelDialog();
      fetchData(); // データを再読み込み
    } catch (error) {
      setError('ホテルの削除に失敗しました');
    }
  };

  // 座標が未設定のホテルを一括補完
  const [backfilling, setBackfilling] = useState(false);
  const handleBackfillHotelCoords = async () => {
    setBackfilling(true);
    try {
      const result = await systemSettingsService.backfillHotelCoords(50);
      setSuccess(result.message || '座標を補完しました');
      fetchData(); // 一覧を再読み込み
    } catch (e) {
      setError('座標の一括補完に失敗しました');
    } finally {
      setBackfilling(false);
    }
  };

  // ===== 待機場所マスタ =====
  const fetchWaitingLocations = async () => {
    try {
      const list = await waitingLocationService.getWaitingLocations();
      setWaitingLocations(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('待機場所の取得に失敗:', e);
    }
  };

  const handleAddWaiting = async () => {
    if (!newWaiting.name.trim()) {
      setError('場所名を入力してください');
      return;
    }
    setSavingWaiting(true);
    try {
      // 座標はバックエンドが住所からNominatimで補完する
      await waitingLocationService.addWaitingLocation({
        name: newWaiting.name,
        name_kana: newWaiting.name_kana,
        address: newWaiting.address,
      });
      setSuccess('待機場所を追加しました');
      setIsWaitingDialogOpen(false);
      setNewWaiting({ name: '', name_kana: '', address: '' });
      fetchWaitingLocations();
    } catch (e) {
      setError('待機場所の追加に失敗しました');
    } finally {
      setSavingWaiting(false);
    }
  };

  const handleUpdateWaiting = async () => {
    if (!editingWaiting) return;
    setSavingWaiting(true);
    try {
      await waitingLocationService.updateWaitingLocation(editingWaiting.id, {
        name: editingWaiting.name,
        name_kana: editingWaiting.name_kana || '',
        address: editingWaiting.address || '',
        // 住所を変えた場合は座標をnullで送り、バックエンドで再補完させる
        latitude: editingWaiting.latitude ?? null,
        longitude: editingWaiting.longitude ?? null,
      });
      setSuccess('待機場所を更新しました');
      setIsEditWaitingDialogOpen(false);
      setEditingWaiting(null);
      fetchWaitingLocations();
    } catch (e) {
      setError('待機場所の更新に失敗しました');
    } finally {
      setSavingWaiting(false);
    }
  };

  const handleDeleteWaiting = async (loc: WaitingLocation) => {
    try {
      await waitingLocationService.deleteWaitingLocation(loc.id);
      setWaitingLocations((prev) => prev.filter((w) => w.id !== loc.id));
      setSuccess('待機場所を削除しました');
    } catch (e) {
      setError('待機場所の削除に失敗しました');
    }
  };


  const handleAddNewOption = async () => {
    if (!newOption.category.trim() || !newOption.name.trim() || !newOption.price) {
      notification.error({ message: 'すべての項目を入力してください。' });
      return;
    }
    try {
      const optionData = {
        ...newOption,
        price: Number(newOption.price),
        castback: Number(newOption.castback || 0),
      };
      await systemSettingsService.addOption(optionData);
      setSuccess('オプションを追加しました');
      setIsOptionDialogOpen(false);
      setNewOption({ name: '', price: 0, category: 'standard', description: '', castback: 0 });
      fetchData(); // データを再読み込み
    } catch (error: any) {
      console.error('オプションの追加に失敗しました:', error);
      notification.error({
        message: 'オプションの追加に失敗しました',
        description: error.response?.data?.message || error.message,
      });
    }
  };

  const formatTime = (timeStr: string | undefined | null): string => {
    if (!timeStr) return '';
    
    // "HH:mm:ss" や "H:mm:ss" 形式に対応
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      const hours = parts[0].padStart(2, '0');
      const minutes = parts[1].padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    
    // 上記で処理できない場合は、元の文字列をそのまま返す
    return timeStr;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsResponse, courses, hotels, options, businessHours] = await Promise.all([
        systemSettingsService.getSystemSettings(),
        systemSettingsService.getCourses(),
        systemSettingsService.getHotels(),
        systemSettingsService.getOptions(),
        systemSettingsService.getBusinessHours(),
      ]);

      // --- デバッグ用のログを追加 ---
      console.log("----- API Response Data -----");
      console.log("Settings:", settingsResponse);
      console.log("Courses:", courses);
      console.log("Hotels:", hotels);
      console.log("Options:", options);
      console.log("Business Hours:", businessHours);
      // --------------------------------

      let settingsData: any = {};
      if (settingsResponse && settingsResponse.data) {
        settingsData = settingsResponse.data;
      }

      // システム設定レスポンス(スネークケース・トップレベル)から、
      // 画面が参照する lineSettings(ネスト・キャメルケース)へマッピングする。
      // これをしないと保存しても再表示時に入力欄が空に戻る。
      const mappedLineSettings = {
        channelId: settingsData.line_channel_id || '',
        channelSecret: settingsData.line_channel_secret || '',
        accessToken: settingsData.line_channel_token || '',
        enabled: !!settingsData.line_notifications_enabled,
        notifyOnNewOrder: !!settingsData.notify_new_orders,
        notifyOnOrderStatus: !!settingsData.notify_order_status,
        notifyOnShiftChange: !!settingsData.notify_shift_change,
      };

      // 通知設定(ネスト・キャメルケース)も、システム設定レスポンスから復元する。
      const mappedNotificationSettings = {
        email: !!settingsData.email_notifications_enabled,
        line: !!settingsData.line_notifications_enabled,
        sms: !!settingsData.sms_notifications_enabled,
        orderNotifications: !!settingsData.notify_new_orders,
        shiftNotifications: !!settingsData.notify_shift_change,
      };

      setFormData(prev => ({
        ...prev,
        ...settingsData,
        lineSettings: {
          ...prev.lineSettings,
          ...mappedLineSettings,
        },
        notificationSettings: {
          ...prev.notificationSettings,
          ...mappedNotificationSettings,
        },
        courses: courses || [],
        hotels: hotels || [],
        options: options || [],
        business_hours: businessHours || [],
      }));

    } catch (error) {
      console.error('設定データの取得に失敗しました。', error);
      notification.error({
        message: 'データ取得エラー',
        description: '設定データの取得に失敗しました。ページをリロードして再度お試しください。'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchWaitingLocations();
  }, []);

  // LINEタブ(index=2)が表示されたとき、連携データを取得する
  const fetchLineLinkData = async () => {
    setLineLoading(true);
    try {
      const [followers, linked, usersResp] = await Promise.allSettled([
        getPendingFollowers(),
        getLinkedUsers(),
        userService.getUsers(1, 0), // 全ユーザーを取得
      ]);
      if (followers.status === 'fulfilled') setPendingFollowers(followers.value);
      if (linked.status === 'fulfilled') setLinkedUsers(linked.value);
      if (usersResp.status === 'fulfilled') {
        const users = (usersResp.value as any)?.users || usersResp.value || [];
        setAllUsers(Array.isArray(users) ? users : []);
      }
    } catch (e) {
      console.error('LINE連携データ取得エラー:', e);
    } finally {
      setLineLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 2) {
      fetchLineLinkData();
    }
  }, [tabValue]);

  const prepareSystemSettings = (): any => {
    const preparedSettings: any = {
      // 基本情報
      store_name: formData.store_name || '',
      phone_number: formData.phone_number || '',
      store_address: formData.store_address || '',
      language: formData.language || 'ja',
      theme: formData.theme || 'light',
      default_map_latitude: Number(formData.default_map_latitude) || 0,
      default_map_longitude: Number(formData.default_map_longitude) || 0,
      default_map_radius: Number(formData.default_map_radius) || 5000, // デフォルト5km
      
      // 通知設定
      notification_enabled: formData.notification_enabled || false,
      map_default_zoom: Number(formData.map_default_zoom) || 13,
      gps_tracking_interval: Number(formData.gps_tracking_interval) || 60,
      line_notification_enabled: formData.line_notification_enabled || false,
      line_account: formData.line_account || '',
      notify_new_orders: formData.notify_new_orders || false,
      notify_shifts: formData.notify_shifts || false,
      notify_messages: formData.notify_messages || false,
      
      // システム設定
      maintenance_mode: formData.maintenance_mode || false,
      system_email: formData.system_email || '',
      backup_enabled: formData.backup_enabled || false,
      backup_frequency: formData.backup_frequency || 'daily',
      backup_retention_days: Number(formData.backup_retention_days) || 30,
      log_level: formData.log_level || 'info',
      max_upload_size: Number(formData.max_upload_size) || 10,
      allowed_file_types: formData.allowed_file_types || ['jpg', 'png', 'pdf'],
      business_hours_start: formData.business_hours_start || '09:00',
      business_hours_end: formData.business_hours_end || '22:00',
      
      // 配列データ
      courses: Array.isArray(formData.courses) ? formData.courses : [],
      hotels: Array.isArray(formData.hotels) ? formData.hotels : [],
      options: Array.isArray(formData.options) ? formData.options : [],
      business_hours: Array.isArray(formData.business_hours) 
        ? formData.business_hours.map(hour => ({
            ...hour,
            day_of_week: Number(hour.day_of_week)
          }))
        : []
    };
    
    console.log('APIに送信する準備ができたデータ:', preparedSettings);
    return preparedSettings;
  };

  // APIに送信する前にフォーマットを変換
  const prepareNotificationSettings = (): any => {
    const ns = formData.notificationSettings || {
      email: false,
      line: false,
      slack: false,
      orderNotifications: false,
      shiftNotifications: false
    };
    // オーダー通知・シフト通知も含めて送信する（除外すると保存されず再表示時に戻ってしまう）
    return {
      email: !!ns.email,
      line: !!ns.line,
      orderNotifications: !!(ns as any).orderNotifications,
      shiftNotifications: !!(ns as any).shiftNotifications,
    };
  };

  // APIに送信する前にフォーマットを変換
  const prepareLineSettings = (): any => {
    const ls = formData.lineSettings || {
      channelId: '',
      channelSecret: '',
      accessToken: '',
      enabled: false,
      notifyOnNewOrder: false,
      notifyOnOrderStatus: false,
      notifyOnShiftChange: false
    };
    // 有効化・各通知スイッチも含めて送信する（除外すると保存されず再表示時に戻ってしまう）
    return {
      channelId: ls.channelId || '',
      channelSecret: ls.channelSecret || '',
      accessToken: ls.accessToken || '',
      enabled: !!ls.enabled,
      notifyOnNewOrder: !!ls.notifyOnNewOrder,
      notifyOnOrderStatus: !!ls.notifyOnOrderStatus,
      notifyOnShiftChange: !!ls.notifyOnShiftChange,
    };
  };

  // APIに送信する前にフォーマットを変換
  const prepareBackupSettings = (): BackupSettings => {
    const { backupTime, retentionDays, backups, ...apiSettings } = formData.backupSettings || {
      autoBackup: true,
      backupInterval: 1,
      maxBackups: 5,
      backupTime: '00:00',
      retentionDays: 30,
      backups: []
    };
    return apiSettings;
  };

  const updateCourseField = (id: string, field: keyof CourseItem, value: number | string) => {
    setFormData(prev => ({
      ...prev,
      courses: prev.courses.map(course => 
        course.id === id ? { ...course, [field]: value } : course
      )
    }));
  };

  // 保存ボタンクリック時の処理 - コース設定
  const saveCourses = async () => {
    try {
      setSavingCourses(true);
      setError(null);

      for (const course of formData.courses) {
        // 'id' が 'new_' で始まる場合は新規作成、それ以外は更新
        if (String(course.id).startsWith('new_')) {
          const { id, ...courseData } = course; // 一時IDを除外
          await systemSettingsService.createCourse(courseData);
        } else {
          await systemSettingsService.updateCourse(course.id, course);
        }
      }

      notification.success({
        message: 'コース設定を保存しました',
      });

      // 最新のコース一覧を再取得してフォームデータを更新
      const updatedCoursesResponse = await systemSettingsService.getCourses();
      setFormData(prev => ({
        ...prev,
        courses: (updatedCoursesResponse as any)?.data || [],
      }));

    } catch (error: any) {
      console.error('コース設定の保存に失敗:', error);
      setError(error.message || 'コース設定の保存に失敗しました');
      notification.error({
        message: 'コース設定の保存に失敗しました',
        description: error.message,
      });
    } finally {
      setSavingCourses(false);
    }
  };

  // コースフォームのリセット
  const resetCourses = async () => {
    try {
      const courses = await systemSettingsService.getCourses();
      console.log('コース設定をリセットしました:', courses);
      setFormData(prev => ({
        ...prev,
        courses: courses
      }));
    } catch (error) {
      console.error('コース設定のリセット中にエラーが発生しました:', error);
      notification.error({
        message: 'コース設定のリセットに失敗しました',
      });
    }
  };

  const saveBusinessHours = async () => {
    // このボタンも基本設定の保存と同じ機能を持たせるため、
    // handleSaveSystemSettings を呼び出す。
    console.log('「営業時間を保存」がクリックされました。基本設定を保存します。');
    await handleSaveSystemSettings();
  };

  // 既存のシステム設定保存ロジックを更新
  const handleSaveSystemSettings = async () => {
    setLoading(true);
    try {
      // business_hours 配列は更新対象から除外し、
      // start と end のみを含むオブジェクトを送信
      const settingsToUpdate = { ...formData };
      delete settingsToUpdate.business_hours; 
      delete settingsToUpdate.courses;
      delete settingsToUpdate.hotels;
      delete settingsToUpdate.options;

      await systemSettingsService.updateSystemSettings(settingsToUpdate);
      
      notification.success({
        message: '基本設定の保存成功',
        description: 'システム設定が正常に更新されました。',
      });
      fetchData(); // データを再取得してUIを更新
    } catch (error) {
      console.error('システム設定の保存に失敗:', error);
      notification.error({
        message: '基本設定の保存失敗',
        description: '設定の保存中にエラーが発生しました。',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    try {
      const apiSettings = prepareNotificationSettings();
      await systemSettingsService.updateNotificationSettings(apiSettings);
      setSuccess('通知設定を保存しました');
    } catch (err) {
      setError('通知設定の保存に失敗しました');
    }
  };

  const handleSaveLineSettings = async () => {
    try {
      const apiSettings = prepareLineSettings();
      await systemSettingsService.updateLineSettings(apiSettings);
      setSuccess('LINE設定を保存しました');
    } catch (err) {
      setError('LINE設定の保存に失敗しました');
    }
  };

  const handleTestLine = async () => {
    try {
      await systemSettingsService.testLineConnection();
      setSuccess('LINEの接続テストに成功しました');
    } catch (err) {
      setError('LINEの接続テストに失敗しました');
    }
  };

  // LINE連携: 紐付けダイアログを開く
  const handleOpenLinkDialog = (follower: PendingFollower) => {
    setLinkingTarget({ follower, userId: null });
    setLinkDialogOpen(true);
  };

  // LINE連携: 紐付けを実行
  const handleLinkUser = async () => {
    if (!linkingTarget.userId || !linkingTarget.follower.line_user_id) return;
    try {
      await linkUser(linkingTarget.userId, linkingTarget.follower.line_user_id);
      setSuccess('LINE連携を設定しました');
      setLinkDialogOpen(false);
      fetchLineLinkData(); // 再取得
    } catch (err) {
      setError('LINE連携の設定に失敗しました');
    }
  };

  // LINE連携: 解除
  const handleUnlinkUser = async (userId: number) => {
    if (!window.confirm('このユーザーのLINE連携を解除しますか？')) return;
    try {
      await unlinkUser(userId);
      setSuccess('LINE連携を解除しました');
      fetchLineLinkData();
    } catch (err) {
      setError('LINE連携の解除に失敗しました');
    }
  };

  const handleSaveBackupSettings = async () => {
    try {
      const apiSettings = prepareBackupSettings();
      await systemSettingsService.updateBackupSettings(apiSettings);
      setSuccess('バックアップ設定を保存しました');
    } catch (err) {
      setError('バックアップ設定の保存に失敗しました');
    }
  };

  const handleCreateBackup = async () => {
    try {
      await systemSettingsService.createBackup();
      fetchData(); // バックアップリストを更新
      setSuccess('バックアップを作成しました');
    } catch (err) {
      setError('バックアップの作成に失敗しました');
    }
  };

  const handleRestoreBackup = async (backupId: string) => {
    setConfirmDialog(true);
    setConfirmAction(() => async () => {
      try {
        await systemSettingsService.restoreBackup(backupId);
        setSuccess('バックアップを復元しました');
        fetchData();
      } catch (err) {
        setError('バックアップの復元に失敗しました');
      }
      setConfirmDialog(false);
    });
  };

  const handleDeleteBackup = async (backupId: string) => {
    try {
      setLoading(true);
      const result = await systemSettingsService.deleteBackup(backupId);
      
      if (result.success) {
        // 成功メッセージを表示
        setSuccess('バックアップが削除されました');
        
        // バックアップ設定を再取得（バックアップ一覧を更新するため）
        const updatedSettings = await systemSettingsService.getBackupSettings();
        // 既存のフォームデータと新しいバックアップ一覧をマージ
        setFormData(prev => ({
          ...prev,
          backupSettings: {
            ...prev.backupSettings,
          backups: updatedSettings.backups || []
          }
        }));
      } else {
        // エラーメッセージを表示
        setError(result.message || 'バックアップの削除に失敗しました');
      }
    } catch (error: any) {
      console.error('バックアップ削除中にエラーが発生しました:', error);
      setError(error.message || 'バックアップの削除中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // 住所から緯度経度を取得する関数を追加
  const geocodeAddress = async (address: string) => {
    try {
      console.log('住所のジオコーディングを開始:', address);
      
      // バックエンドのプロキシエンドポイントを使用
      const response = await axios.get(
        `/api/geocode?address=${encodeURIComponent(address)}`,
        {
          params: {
            language: 'ja'
          }
        }
      );
      
      if (response.data && response.data.location) {
        const location = response.data.location;
        console.log('ジオコーディング結果:', location);
        return {
          lat: location.lat,
          lng: location.lng
        };
      } else {
        console.warn('住所のジオコーディングに失敗:', response.data);
        return null;
      }
    } catch (error) {
      console.error('ジオコーディングAPIエラー:', error);
      return null;
    }
  };

  // 住所入力ハンドラを更新
  const handleAddressChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const address = e.target.value;
    
    // まず住所を更新
    setFormData(prev => ({ ...prev, store_address: address }));
    
    // 住所が十分な長さなら緯度経度を取得
    if (address.length > 5) {
      try {
        const coords = await geocodeAddress(address);
        if (coords) {
          setFormData(prev => ({
            ...prev,
            store_address: address,
            default_map_latitude: coords.lat,
            default_map_longitude: coords.lng
          }));
        }
      } catch (error) {
        console.error('住所から座標を取得できませんでした:', error);
      }
    }
  };

  // 新しいコースを追加する関数
  const addCourse = () => {
    setFormData(prev => {
      // 新規コース用の一時的なIDを生成
      const newId = `new_${Date.now()}`;
      
      return {
        ...prev,
        courses: [
          ...prev.courses,
          { id: newId, name: '新しいコース', duration: 60, castback: 10000, revenue: 25000, is_active: true }
        ]
      };
    });
  };

  // コースを削除する関数
  const removeCourse = (id: string) => {
    setFormData(prev => ({
      ...prev,
      courses: prev.courses.filter(course => course.id !== id)
    }));
  };

  // オプション削除ハンドラ
  const handleOpenDeleteOptionDialog = (option: OptionSetting) => {
    setOptionToDelete(option);
    setIsDeleteOptionDialogOpen(true);
  };

  const handleCloseDeleteOptionDialog = () => {
    setIsDeleteOptionDialogOpen(false);
    setOptionToDelete(null);
  };

  const handleDeleteOption = async () => {
    if (!optionToDelete) return;
    try {
      await systemSettingsService.deleteOption(optionToDelete.id);
      setSuccess('オプションを削除しました');
      handleCloseDeleteOptionDialog();
      fetchData(); // データを再読み込み
    } catch (error) {
      setError('オプションの削除に失敗しました');
    }
  };

  // オプション編集ハンドラ
  const handleOpenEditOptionDialog = (option: OptionSetting) => {
    setEditingOption(option);
    setIsEditOptionDialogOpen(true);
  };

  const handleCloseEditOptionDialog = () => {
    setIsEditOptionDialogOpen(false);
    setEditingOption(null);
  };

  const handleUpdateOption = async () => {
    if (!editingOption) return;
    try {
      await systemSettingsService.updateOption(editingOption.id, editingOption);
      setSuccess('オプションを更新しました');
      handleCloseEditOptionDialog();
      fetchData(); // データを再読み込み
    } catch (error) {
      setError('オプションの更新に失敗しました');
    }
  };


  // システム設定のレンダリング関数を更新
  const renderSystemSettings = () => (
    <Grid container spacing={3}>
      {/* 店舗基本情報セクション */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>店舗基本情報</Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="店舗名"
          value={formData.store_name}
          onChange={(e) =>
            setFormData({ ...formData, store_name: e.target.value })
          }
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="電話番号"
          value={formData.phone_number}
            onChange={(e) =>
            setFormData({ ...formData, phone_number: e.target.value })
          }
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="住所"
          value={formData.store_address}
          onChange={handleAddressChange}
          helperText="住所を入力すると自動的に緯度・経度が計算されます"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>言語</InputLabel>
          <Select
            value={formData.language}
            label="言語"
            onChange={(e) =>
              setFormData({ ...formData, language: e.target.value })
            }
          >
            <MenuItem value="ja">日本語</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>テーマ</InputLabel>
          <Select
            value={formData.theme}
            label="テーマ"
            onChange={(e) =>
              setFormData({ ...formData, theme: e.target.value })
            }
          >
            <MenuItem value="light">ライト</MenuItem>
            <MenuItem value="dark">ダーク</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="デフォルトマップ緯度"
          type="number"
          value={formData.default_map_latitude}
          onChange={(e) =>
            setFormData({
              ...formData,
              default_map_latitude: Number(e.target.value),
            })
          }
          InputProps={{
            readOnly: false, // 手動でも編集可能
          }}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="デフォルトマップ経度"
          type="number"
          value={formData.default_map_longitude}
          onChange={(e) =>
            setFormData({
              ...formData,
              default_map_longitude: Number(e.target.value),
            })
          }
          InputProps={{
            readOnly: false, // 手動でも編集可能
          }}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="デフォルトマップ半径"
          type="number"
          value={formData.default_map_radius}
          onChange={(e) =>
            setFormData({
              ...formData,
              default_map_radius: Number(e.target.value),
            })
          }
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="GPS追跡間隔（秒）"
          type="number"
          value={formData.gps_tracking_interval}
          onChange={(e) =>
            setFormData({
              ...formData,
              gps_tracking_interval: Number(e.target.value),
            })
          }
        />
      </Grid>
      
      {/* 店舗情報保存ボタン */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveSystemSettings}
          >
            店舗情報を保存
          </Button>
        </Box>
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      
      {/* 営業時間の設定を追加 */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>営業時間</Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="営業開始時間"
          type="time"
          value={formatTime(formData.business_hours_start)}
          onChange={(e) =>
            setFormData({
              ...formData,
              business_hours_start: e.target.value,
            })
          }
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            step: 300, // 5分単位
          }}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="営業終了時間"
          type="time"
          value={formatTime(formData.business_hours_end)}
          onChange={(e) =>
            setFormData({
              ...formData,
              business_hours_end: e.target.value,
            })
          }
          InputLabelProps={{
            shrink: true,
          }}
          inputProps={{
            step: 300, // 5分単位
          }}
        />
      </Grid>
      
      {/* 営業時間保存ボタン */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={saveBusinessHours}
          >
            営業時間を保存
          </Button>
        </Box>
      </Grid>
      
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>
      
      {/* コース設定の繰り返しフィールド */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">コース設定</Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={addCourse}
          >
            コースを追加
          </Button>
        </Box>
        
        {formData.courses.map((course) => (
          <Paper key={course.id} elevation={1} sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="分数"
                  type="number"
                  value={course.duration}
                  onChange={(e) => 
                    updateCourseField(course.id, 'duration', Number(e.target.value))
                  }
                  InputProps={{
                    endAdornment: <span>分</span>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="キャストバック"
                  type="number"
                  value={course.castback}
                  onChange={(e) => 
                    updateCourseField(course.id, 'castback', Number(e.target.value))
                  }
                  InputProps={{
                    startAdornment: <span>¥</span>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="収益"
                  type="number"
                  value={course.revenue}
                  onChange={(e) => 
                    updateCourseField(course.id, 'revenue', Number(e.target.value))
                  }
                  InputProps={{
                    startAdornment: <span>¥</span>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Button 
                  variant="outlined" 
                  color="error" 
                  onClick={() => removeCourse(course.id)}
                  fullWidth
                >
                  削除
                </Button>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Grid>
      
      {/* コース設定保存ボタン */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined" 
            color="primary" 
            onClick={addCourse}
          >
            コースを追加
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={saveCourses}
          >
            コース設定を保存
          </Button>
        </Box>
      </Grid>
    </Grid>
  );

  const renderNotificationSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          通知の種類
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.notificationSettings?.email}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  notificationSettings: {
                    ...formData.notificationSettings,
                  email: e.target.checked,
                  }
                })
              }
            />
          }
          label="メール通知"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
          ユーザーに設定されているメールアドレス宛に通知を送ります
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.notificationSettings?.line}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  notificationSettings: {
                    ...formData.notificationSettings,
                  line: e.target.checked,
                  }
                })
              }
            />
          }
          label="LINE通知"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
          ユーザーに設定されているLINE ID宛に通知を送ります
        </Typography>
      </Grid>
      
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          通知内容
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.notificationSettings?.orderNotifications}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  notificationSettings: {
                    ...formData.notificationSettings,
                  orderNotifications: e.target.checked,
                  }
                })
              }
              disabled={!formData.notificationSettings?.email && !formData.notificationSettings?.line}
            />
          }
          label="オーダー通知"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
          新規オーダーが作成されたときに、オーダー作成画面で選択された「キャスト」「ドライバー」宛に通知を送信します
        </Typography>
      </Grid>
      
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.notificationSettings?.shiftNotifications}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  notificationSettings: {
                    ...formData.notificationSettings,
                  shiftNotifications: e.target.checked,
                  }
                })
              }
              disabled={!formData.notificationSettings?.email && !formData.notificationSettings?.line}
            />
          }
          label="シフト通知"
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
          シフトが登録された際に「管理者」「マネージャー」「内勤スタッフ」「ドライバー」のうち、有効化されているユーザーにのみ通知を送信します
        </Typography>
      </Grid>
      
      {/* 通知設定保存ボタン */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveNotificationSettings}
          >
            通知設定を保存
          </Button>
        </Box>
      </Grid>
    </Grid>
  );

  const renderLineSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.lineSettings?.enabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  lineSettings: {
                    ...formData.lineSettings,
                  enabled: e.target.checked,
                  }
                })
              }
            />
          }
          label="LINE連携を有効化"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Channel ID"
          value={formData.lineSettings?.channelId}
          onChange={(e) =>
            setFormData({
              ...formData,
              lineSettings: {
                ...formData.lineSettings,
                channelId: e.target.value
              }
            })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Channel Secret"
          type="password"
          value={formData.lineSettings?.channelSecret}
          onChange={(e) =>
            setFormData({
              ...formData,
              lineSettings: {
                ...formData.lineSettings,
                channelSecret: e.target.value
              }
            })
          }
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Access Token"
          value={formData.lineSettings?.accessToken}
          onChange={(e) =>
            setFormData({
              ...formData,
              lineSettings: {
                ...formData.lineSettings,
                accessToken: e.target.value
              }
            })
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.lineSettings?.notifyOnNewOrder}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  lineSettings: {
                    ...formData.lineSettings,
                    notifyOnNewOrder: e.target.checked
                  }
                })
              }
            />
          }
          label="新規オーダー通知"
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.lineSettings?.notifyOnOrderStatus}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  lineSettings: {
                    ...formData.lineSettings,
                    notifyOnOrderStatus: e.target.checked
                  }
                })
              }
            />
          }
          label="オーダーステータス変更通知"
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.lineSettings?.notifyOnShiftChange}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  lineSettings: {
                    ...formData.lineSettings,
                    notifyOnShiftChange: e.target.checked
                  }
                })
              }
            />
          }
          label="シフト変更通知"
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<SendIcon />}
          onClick={handleTestLine}
          disabled={!formData.lineSettings?.enabled}
        >
          接続テスト
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveLineSettings}
        >
          LINE設定を保存
        </Button>
      </Grid>

      {/* ===== ユーザー連携（LINE友だちとアプリユーザーの紐付け） ===== */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          ユーザー連携
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          公式アカウントを友だち追加したLINEユーザーが下に表示されます。各ユーザー（キャスト等）に紐付けると、そのLINEへ通知を送れます。
        </Typography>
      </Grid>

      {/* 未連携のLINE友だち */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          未連携のLINE友だち {lineLoading ? '' : `(${pendingFollowers.length})`}
        </Typography>
        {lineLoading ? (
          <CircularProgress size={24} />
        ) : pendingFollowers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            未連携の友だちはいません。対象者に公式アカウントを友だち追加してもらってください。
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>表示名</TableCell>
                  <TableCell>LINEユーザーID</TableCell>
                  <TableCell>追加日時</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingFollowers.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell>{f.display_name || '(名前取得不可)'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{f.line_user_id}</TableCell>
                    <TableCell>{f.followed_at ? format(new Date(f.followed_at), 'MM/dd HH:mm') : '-'}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={() => handleOpenLinkDialog(f)}>
                        ユーザーに紐付け
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>

      {/* 連携済みユーザー */}
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          連携済みユーザー {lineLoading ? '' : `(${linkedUsers.length})`}
        </Typography>
        {linkedUsers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            連携済みのユーザーはいません。
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ユーザー</TableCell>
                  <TableCell>ロール</TableCell>
                  <TableCell>LINEユーザーID</TableCell>
                  <TableCell>状態</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {linkedUsers.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell>{u.full_name || u.username || `ユーザー${u.user_id}`}</TableCell>
                    <TableCell>{u.role || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{u.line_user_id}</TableCell>
                    <TableCell>
                      <Chip label={u.is_enabled ? '有効' : '無効'} size="small" color={u.is_enabled ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" color="error" onClick={() => handleUnlinkUser(u.user_id)}>
                        解除
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  );

  // LINE友だちをユーザーに紐付けるダイアログ
  const renderLinkDialog = () => (
    <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>LINE友だちをユーザーに紐付け</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          表示名: {linkingTarget.follower.display_name || '(名前取得不可)'}<br />
          LINEユーザーID: {linkingTarget.follower.line_user_id}
        </Typography>
        <Autocomplete
          options={allUsers}
          getOptionLabel={(u: any) =>
            `${u.full_name || `${u.last_name || ''} ${u.first_name || ''}`.trim() || u.username} (${u.role || ''})`
          }
          onChange={(_e, value: any) => setLinkingTarget((prev) => ({ ...prev, userId: value ? value.id : null }))}
          renderInput={(params) => <TextField {...params} label="紐付けるユーザーを選択" fullWidth />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setLinkDialogOpen(false)}>キャンセル</Button>
        <Button variant="contained" onClick={handleLinkUser} disabled={!linkingTarget.userId}>
          紐付ける
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderBackupSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.backupSettings?.autoBackup}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  backupSettings: {
                    ...formData.backupSettings,
                    autoBackup: e.target.checked
                  }
                })
              }
            />
          }
          label="自動バックアップ"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>バックアップ間隔</InputLabel>
          <Select
            value={String(formData.backupSettings?.backupInterval)}
            label="バックアップ間隔"
            onChange={(e) =>
              setFormData({
                ...formData,
                backupSettings: {
                  ...formData.backupSettings,
                  backupInterval: Number(e.target.value)
                }
              })
            }
          >
            <MenuItem value="1">毎日</MenuItem>
            <MenuItem value="7">毎週</MenuItem>
            <MenuItem value="30">毎月</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="バックアップ時刻"
          type="time"
          value={formData.backupSettings?.backupTime}
          onChange={(e) =>
            setFormData({
              ...formData,
              backupSettings: {
                ...formData.backupSettings,
                backupTime: e.target.value
              }
            })
          }
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="保持期間（日）"
          type="number"
          value={formData.backupSettings?.retentionDays}
          onChange={(e) =>
            setFormData({
              ...formData,
              backupSettings: {
                ...formData.backupSettings,
              retentionDays: Number(e.target.value),
              }
            })
          }
        />
      </Grid>
      
      {/* バックアップ設定保存ボタンと手動バックアップボタン */}
      <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<BackupIcon />}
          onClick={handleCreateBackup}
        >
          手動バックアップ
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveBackupSettings}
        >
          バックアップ設定を保存
        </Button>
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          バックアップ履歴
        </Typography>
        {formData.backupSettings?.backups && formData.backupSettings.backups.length > 0 ? (
        <List>
          {formData.backupSettings.backups.map((backup) => {
            // UTC日時を日本時間に変換（+9時間）
            const dateUtc = new Date(backup.created_at);
            const dateJst = new Date(dateUtc.getTime() + 9 * 60 * 60 * 1000);
            
            return (
              <React.Fragment key={backup.id}>
                <ListItem>
                  <ListItemText
                    primary={format(dateJst, 'yyyy/MM/dd HH:mm', { locale: ja })}
                    secondary={`サイズ: ${backup.size}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleRestoreBackup(backup.id)}
                    >
                      <RestoreIcon />
                    </IconButton>
                    <IconButton 
                      edge="end"
                      onClick={() => handleDeleteBackup(backup.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </React.Fragment>
            );
          })}
        </List>
        ) : (
          <Typography variant="body2" color="textSecondary">
            バックアップはまだありません
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  const renderHotelSettings = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">ホテル一覧</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="secondary" onClick={handleBackfillHotelCoords} disabled={backfilling}>
            {backfilling ? '補完中...' : '座標を一括補完（50件）'}
          </Button>
          <Button variant="outlined" color="primary" onClick={() => setIsHotelDialogOpen(true)}>
            ホテルを追加
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        座標はルート計算（迎え場所→ホテルの予測時間）に使います。既存ホテルで「座標なし」のものは「座標を一括補完」で住所から取得できます（1回50件。残りがあれば繰り返し実行）。
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ホテル名</TableCell>
              <TableCell>住所</TableCell>
              <TableCell>座標</TableCell>
              <TableCell>電話番号</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formData.hotels.map((hotel) => (
              <TableRow key={hotel.id}>
                <TableCell>{hotel.name}</TableCell>
                <TableCell>{hotel.address}</TableCell>
                <TableCell>
                  {hotel.latitude != null && hotel.longitude != null
                    ? `${Number(hotel.latitude).toFixed(5)}, ${Number(hotel.longitude).toFixed(5)}`
                    : <Chip label="座標なし" size="small" color="warning" />}
                </TableCell>
                <TableCell>{hotel.phone_number}</TableCell>
                <TableCell>
                  <Chip label={hotel.is_active ? '有効' : '無効'} color={hotel.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell>
                  <Button variant="outlined" size="small" sx={{ mr: 1 }} onClick={() => handleOpenEditHotelDialog(hotel)}>編集</Button>
                  <Button variant="outlined" size="small" color="error" onClick={() => handleOpenDeleteHotelDialog(hotel)}>削除</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderWaitingLocationSettings = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">待機場所一覧</Typography>
        <Button variant="outlined" color="primary" onClick={() => setIsWaitingDialogOpen(true)}>
          待機場所を追加
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        よく使う迎え場所を登録します。住所から座標を自動取得（OpenStreetMap）。オーダー作成の迎え場所候補に表示されます。
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>場所名</TableCell>
              <TableCell>ふりがな</TableCell>
              <TableCell>住所</TableCell>
              <TableCell>座標</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {waitingLocations.map((loc) => (
              <TableRow key={loc.id}>
                <TableCell>{loc.name}</TableCell>
                <TableCell>{loc.name_kana}</TableCell>
                <TableCell>{loc.address}</TableCell>
                <TableCell>
                  {loc.latitude != null && loc.longitude != null
                    ? `${Number(loc.latitude).toFixed(5)}, ${Number(loc.longitude).toFixed(5)}`
                    : <Chip label="座標なし" size="small" color="warning" />}
                </TableCell>
                <TableCell>
                  <Button variant="outlined" size="small" sx={{ mr: 1 }} onClick={() => { setEditingWaiting(loc); setIsEditWaitingDialogOpen(true); }}>編集</Button>
                  <Button variant="outlined" size="small" color="error" onClick={() => handleDeleteWaiting(loc)}>削除</Button>
                </TableCell>
              </TableRow>
            ))}
            {waitingLocations.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">待機場所が登録されていません</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderOptionSettings = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">オプション設定</Typography>
        <Button variant="outlined" color="primary" onClick={() => setIsOptionDialogOpen(true)}>
          オプションを追加
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>カテゴリ</TableCell>
              <TableCell>名称</TableCell>
              <TableCell>料金</TableCell>
              <TableCell>キャストバック</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formData.options.map((option) => (
              <TableRow key={option.id}>
                <TableCell>{option.category}</TableCell>
                <TableCell>{option.name}</TableCell>
                <TableCell>{option.price}円</TableCell>
                <TableCell>{option.castback}円</TableCell>
                <TableCell>
                  <Button variant="outlined" size="small" sx={{ mr: 1 }} onClick={() => handleOpenEditOptionDialog(option)}>編集</Button>
                  <Button variant="outlined" size="small" color="error" onClick={() => handleOpenDeleteOptionDialog(option)}>削除</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDialog
        open={isDeleteOptionDialogOpen}
        onClose={handleCloseDeleteOptionDialog}
        onConfirm={handleDeleteOption}
        title="オプションの削除"
        description={`本当に「${optionToDelete?.name}」を削除しますか？この操作は元に戻せません。`}
      />

      {/* オプション編集ダイアログ */}
      <Dialog open={isEditOptionDialogOpen} onClose={handleCloseEditOptionDialog}>
        <DialogTitle>オプションを編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="オプション名"
            type="text"
            fullWidth
            variant="standard"
            value={editingOption?.name || ''}
            onChange={(e) => setEditingOption(prev => prev ? { ...prev, name: e.target.value } : null)}
          />
          <TextField
            margin="dense"
            label="価格"
            type="number"
            fullWidth
            variant="standard"
            value={editingOption?.price || 0}
            onChange={(e) => setEditingOption(prev => prev ? { ...prev, price: Number(e.target.value) } : null)}
          />
          <TextField
            margin="dense"
            label="カテゴリ"
            type="text"
            fullWidth
            variant="standard"
            value={editingOption?.category || ''}
            onChange={(e) => setEditingOption(prev => prev ? { ...prev, category: e.target.value } : null)}
          />
          <TextField
            margin="dense"
            label="説明"
            type="text"
            fullWidth
            variant="standard"
            value={editingOption?.description || ''}
            onChange={(e) => setEditingOption(prev => prev ? { ...prev, description: e.target.value } : null)}
          />
          <TextField
            margin="dense"
            label="キャストバック"
            type="number"
            fullWidth
            variant="standard"
            value={editingOption?.castback || 0}
            onChange={(e) => setEditingOption(prev => prev ? { ...prev, castback: Number(e.target.value) } : null)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditOptionDialog}>キャンセル</Button>
          <Button onClick={handleUpdateOption}>保存</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" sx={{ mb: 4 }}>
          システム設定
        </Typography>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="基本設定" />
            <Tab label="通知設定" />
            <Tab label="LINE設定" />
            <Tab label="バックアップ" />
            <Tab label="ホテル一覧" />
            <Tab label="オプション" />
            <Tab label="待機場所" />
          </Tabs>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          {tabValue === 0 && (
            <>
              {renderSystemSettings()}
            </>
          )}
          {tabValue === 1 && (
            <>
              {renderNotificationSettings()}
            </>
          )}
          {tabValue === 2 && (
            <>
              {renderLineSettings()}
            </>
          )}
          {tabValue === 3 && (
            <>
              {renderBackupSettings()}
            </>
          )}
          {tabValue === 4 && (
            <>
              {renderHotelSettings()}
            </>
          )}
          {tabValue === 5 && (
            <>
              {renderOptionSettings()}
            </>
          )}
          {tabValue === 6 && (
            <>
              {renderWaitingLocationSettings()}
            </>
          )}
        </Paper>

        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>

        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
          <DialogTitle>確認</DialogTitle>
          <DialogContent>
            バックアップを復元すると、現在のデータは上書きされます。
            続行してもよろしいですか？
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(false)}>キャンセル</Button>
            <Button onClick={confirmAction || (() => {})} variant="contained" color="primary">
              復元
            </Button>
          </DialogActions>
        </Dialog>

        {/* 待機場所 追加ダイアログ */}
        <Dialog open={isWaitingDialogOpen} onClose={() => setIsWaitingDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>待機場所を追加</DialogTitle>
          <DialogContent>
            <TextField autoFocus margin="dense" label="場所名" type="text" fullWidth variant="standard" value={newWaiting.name} onChange={(e) => setNewWaiting({ ...newWaiting, name: e.target.value })} />
            <TextField margin="dense" label="ふりがな（かな検索用）" type="text" fullWidth variant="standard" value={newWaiting.name_kana} onChange={(e) => setNewWaiting({ ...newWaiting, name_kana: e.target.value })} />
            <TextField margin="dense" label="住所" type="text" fullWidth variant="standard" value={newWaiting.address} onChange={(e) => setNewWaiting({ ...newWaiting, address: e.target.value })} helperText="住所から座標を自動取得します（OpenStreetMap）" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsWaitingDialogOpen(false)} disabled={savingWaiting}>キャンセル</Button>
            <Button onClick={handleAddWaiting} disabled={savingWaiting}>{savingWaiting ? '保存中...' : '追加'}</Button>
          </DialogActions>
        </Dialog>

        {/* 待機場所 編集ダイアログ */}
        <Dialog open={isEditWaitingDialogOpen} onClose={() => { setIsEditWaitingDialogOpen(false); setEditingWaiting(null); }} fullWidth maxWidth="sm">
          <DialogTitle>待機場所を編集</DialogTitle>
          <DialogContent>
            <TextField autoFocus margin="dense" label="場所名" type="text" fullWidth variant="standard" value={editingWaiting?.name || ''} onChange={(e) => setEditingWaiting(prev => prev ? { ...prev, name: e.target.value } : null)} />
            <TextField margin="dense" label="ふりがな（かな検索用）" type="text" fullWidth variant="standard" value={editingWaiting?.name_kana || ''} onChange={(e) => setEditingWaiting(prev => prev ? { ...prev, name_kana: e.target.value } : null)} />
            <TextField margin="dense" label="住所" type="text" fullWidth variant="standard" value={editingWaiting?.address || ''} onChange={(e) => setEditingWaiting(prev => prev ? { ...prev, address: e.target.value, latitude: null, longitude: null } : null)} helperText="住所を変更すると座標を再取得します" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setIsEditWaitingDialogOpen(false); setEditingWaiting(null); }} disabled={savingWaiting}>キャンセル</Button>
            <Button onClick={handleUpdateWaiting} disabled={savingWaiting}>{savingWaiting ? '保存中...' : '更新'}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isHotelDialogOpen} onClose={() => setIsHotelDialogOpen(false)}>
          <DialogTitle>ホテルを追加</DialogTitle>
          <DialogContent>
            <TextField autoFocus margin="dense" label="ホテル名" type="text" fullWidth variant="standard" value={newHotel.name} onChange={(e) => setNewHotel({ ...newHotel, name: e.target.value })} />
            <TextField margin="dense" label="ふりがな" type="text" fullWidth variant="standard" value={newHotel.name_kana} onChange={(e) => setNewHotel({ ...newHotel, name_kana: e.target.value })} />
            <TextField margin="dense" label="住所" type="text" fullWidth variant="standard" value={newHotel.address} onChange={(e) => setNewHotel({ ...newHotel, address: e.target.value })} />
            <TextField margin="dense" label="電話番号" type="text" fullWidth variant="standard" value={newHotel.phone_number} onChange={(e) => setNewHotel({ ...newHotel, phone_number: e.target.value })} />
            <TextField margin="dense" label="説明" type="text" fullWidth multiline rows={3} variant="standard" value={newHotel.description} onChange={(e) => setNewHotel({ ...newHotel, description: e.target.value })} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsHotelDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleAddNewHotel}>追加</Button>
          </DialogActions>
        </Dialog>

        {/* ホテル編集ダイアログ */}
        <Dialog open={isEditHotelDialogOpen} onClose={handleCloseEditHotelDialog}>
          <DialogTitle>ホテル情報を編集</DialogTitle>
          <DialogContent>
            <TextField autoFocus margin="dense" label="ホテル名" type="text" fullWidth variant="standard" value={editingHotel?.name || ''} onChange={(e) => setEditingHotel(prev => prev ? { ...prev, name: e.target.value } : null)} />
            <TextField margin="dense" label="ふりがな" type="text" fullWidth variant="standard" value={editingHotel?.name_kana || ''} onChange={(e) => setEditingHotel(prev => prev ? { ...prev, name_kana: e.target.value } : null)} />
            <TextField margin="dense" label="住所" type="text" fullWidth variant="standard" value={editingHotel?.address || ''} onChange={(e) => setEditingHotel(prev => prev ? { ...prev, address: e.target.value } : null)} />
            <TextField margin="dense" label="電話番号" type="text" fullWidth variant="standard" value={editingHotel?.phone_number || ''} onChange={(e) => setEditingHotel(prev => prev ? { ...prev, phone_number: e.target.value } : null)} />
            <TextField margin="dense" label="説明" type="text" fullWidth multiline rows={3} variant="standard" value={editingHotel?.description || ''} onChange={(e) => setEditingHotel(prev => prev ? { ...prev, description: e.target.value } : null)} />

            {/* 座標（手動調整可）。ジオコーディングがズレた場合はここで直接編集する */}
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>座標（ルート計算に使用。ズレている場合は手動で調整）</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <TextField
                  margin="dense" label="緯度 (latitude)" type="number" variant="standard"
                  value={editingHotel?.latitude ?? ''}
                  onChange={(e) => setEditingHotel(prev => prev ? { ...prev, latitude: e.target.value === '' ? null : Number(e.target.value) } : null)}
                  inputProps={{ step: 'any' }}
                  sx={{ width: 160 }}
                />
                <TextField
                  margin="dense" label="経度 (longitude)" type="number" variant="standard"
                  value={editingHotel?.longitude ?? ''}
                  onChange={(e) => setEditingHotel(prev => prev ? { ...prev, longitude: e.target.value === '' ? null : Number(e.target.value) } : null)}
                  inputProps={{ step: 'any' }}
                  sx={{ width: 160 }}
                />
                <Button size="small" variant="outlined" onClick={handleRegeocodeHotel} disabled={regeocoding} sx={{ mb: 0.5 }}>
                  {regeocoding ? '取得中...' : '住所から再取得'}
                </Button>
              </Box>
              {editingHotel?.latitude != null && editingHotel?.longitude != null && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  <a
                    href={`https://www.google.com/maps?q=${editingHotel.latitude},${editingHotel.longitude}`}
                    target="_blank" rel="noreferrer"
                  >
                    地図でこの座標を確認する
                  </a>
                  {' '}（ズレていればGoogleマップ等で正しい緯度経度を調べて上書きできます）
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={<Switch checked={editingHotel?.is_active || false} onChange={(e) => setEditingHotel(prev => prev ? { ...prev, is_active: e.target.checked } : null)} />}
              label="有効"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditHotelDialog}>キャンセル</Button>
            <Button onClick={handleUpdateHotel}>保存</Button>
          </DialogActions>
        </Dialog>

        {/* ホテル削除確認ダイアログ */}
        <ConfirmDialog
          open={isDeleteHotelDialogOpen}
          onClose={handleCloseDeleteHotelDialog}
          onConfirm={handleDeleteHotel}
          title="ホテルの削除"
          description={`本当に「${hotelToDelete?.name}」を削除しますか？この操作は元に戻せません。`}
        />

        <Dialog open={isOptionDialogOpen} onClose={() => setIsOptionDialogOpen(false)}>
          <DialogTitle>新しいオプションを追加</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="カテゴリ"
              type="text"
              fullWidth
              variant="standard"
              value={newOption.category}
              onChange={(e) => setNewOption({ ...newOption, category: e.target.value })}
            />
            <TextField
              margin="dense"
              label="名称"
              type="text"
              fullWidth
              variant="standard"
              value={newOption.name}
              onChange={(e) => setNewOption({ ...newOption, name: e.target.value })}
            />
            <TextField
              margin="dense"
              label="料金"
              type="number"
              fullWidth
              variant="standard"
              value={newOption.price}
              onChange={(e) => setNewOption({ ...newOption, price: Number(e.target.value) })}
            />
            <TextField
              margin="dense"
              label="キャストバック"
              type="number"
              fullWidth
              variant="standard"
              value={newOption.castback}
              onChange={(e) => setNewOption({ ...newOption, castback: Number(e.target.value) })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsOptionDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleAddNewOption}>追加</Button>
          </DialogActions>
        </Dialog>

        {/* 通知コンポーネントを追加 */}
        <NotificationComponent />
        {renderLinkDialog()}
      </Box>
    </DashboardLayout>
  );
};

export default SystemSettings;
